import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';

interface UseAuthStatusOptions {
  redirectTo?: string;
  redirectIfAuthenticated?: boolean;
}

/**
 * Custom hook to handle authentication status and redirects
 * 
 * @param options Configuration options
 * @param options.redirectTo Path to redirect to if authentication status doesn't match requirements
 * @param options.redirectIfAuthenticated If true, redirect when authenticated; if false, redirect when not authenticated
 * @returns Authentication status information
 */
export function useAuthStatus(options: UseAuthStatusOptions = {}) {
  const { 
    redirectTo = '/admin/login',
    redirectIfAuthenticated = false
  } = options;
  
  const { isAuthenticated, refreshAccessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuthStatus = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // If not authenticated, try to refresh the token
        if (!isAuthenticated) {
          const refreshed = await refreshAccessToken();
          
          // If we should redirect when not authenticated and refresh failed
          if (!redirectIfAuthenticated && !refreshed) {
            // Store the current location to redirect back after login
            navigate(redirectTo, { 
              state: { from: location.pathname },
              replace: true 
            });
            return;
          }
        } else if (redirectIfAuthenticated) {
          // If we should redirect when authenticated and user is authenticated
          navigate(redirectTo, { replace: true });
          return;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication error');
        
        if (!redirectIfAuthenticated) {
          navigate(redirectTo, { 
            state: { from: location.pathname },
            replace: true 
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthStatus();
  }, [isAuthenticated, redirectIfAuthenticated, redirectTo, navigate, location.pathname, refreshAccessToken]);

  return {
    isAuthenticated,
    isLoading,
    error
  };
}
